import React from "react";

import SimManager from "../managers/simManager";

//@ts-expect-error Browser Specific Type
const isFirefox = typeof InstallTrigger !== "undefined";
import controls from "../helpers/controls";
import "./styles.scss";

import { CustomObjectProps, CustomObjectState } from "../types/SceneTypes";
import { Vector3, StateObject, UserInput } from "../types";
import { calculateXOffset, calculateYOffset } from "../helpers/responsiveness";
import ComponentFactory from "../factories/componentFactory";
import { InteractableButtons } from "./interactableButtons";
import MoveMenu from "../modals/moveMenu/moveMenu";

class CustomObject extends React.Component<
  CustomObjectProps,
  CustomObjectState
> {
  private controls = new controls();
  private states: any;
  private currentState: string;
  private userInput: any;
  // eslint-disable-next-line
  private component: JSX.Element | null;
  private onDropItems: any;
  private moveMenuRef = React.createRef<MoveMenu>();
  private hamburgerButtonRef = React.createRef<HTMLElement>();

  constructor(props: any) {
    super(props);
    this.currentState = props.defaultState;
    this.component = null;
    this.onDropItems = props.onDropItems;
    this.state = {
      position: this.props.position,
      currentState: props.defaultState,
      sceneScale: props.sceneScale,
      userScale: props.userScale,
    };
  }

  componentDidMount(): void {
    SimManager.getInstance().registerCustomObject(this);
    this.component = ComponentFactory.getInstance().getComponent(
      this.props.customComponentID,
      this.props.id,
      { moveMenu: this.props.moveMenu }
    );

    //Setting move menu options from props
    if (this.props.moveMenu) {
      const moveMenuOptions = Object.keys(this.props.moveMenu);
      this.moveMenuRef.current?.setMoveMenuOptions(moveMenuOptions);
    }
  }

  setHamburgerButtonRef = (ref: React.RefObject<HTMLElement>) => {
    this.hamburgerButtonRef = ref;
    this.moveMenuRef.current?.setHamburgerButtonRef(ref);
  };

  getId(): string {
    return this.props.id;
  }

  getName(): string {
    return this.props.name;
  }

  setItemPosition(position?: Vector3) {
    if (position) {
      this.setState({ position: position });
    }
  }

  updateAltText = (text: string) => {
    this.setState({ ariaLabel: text });
  };

  getItemPosition(): Vector3 {
    return this.state.position;
  }

  setItemState(postState: string) {
    // If states are not defined for this custom object, just update the current state
    if (!this.states) {
      this.setState({ currentState: postState });
      this.currentState = postState;
      SimManager.getInstance().handleUpdateItems();
      return;
    }

    const changeState = this.states.filter(
      (x: { name: string }) => x.name === postState
    );

    if (changeState.length > 0) {
      this.setState({ currentState: postState });
      this.currentState = postState;
      SimManager.getInstance().handleUpdateItems();
    }
  }

  getCurrentState(): string {
    return this.currentState;
  }

  updateUserInput(newValuePair: UserInput) {
    this.userInput = { ...this.userInput, ...newValuePair };
    SimManager.getInstance().handleUpdateItems();
  }

  getCurrentInput() {
    return this.userInput;
  }

  setScale(userScale: number, sceneScale: number) {
    this.setState({ userScale: userScale, sceneScale: sceneScale });
  }

  moveMenuPressed = () => {
    this.moveMenuRef.current?.openMoveMenu();
  };

  removeMoveMenuButton() {
    // CustomObject doesn't need to implement this since it doesn't have showLabel state
    // But we provide it for interface compatibility
  }

  translationX = (x: number) => {
    return Math.floor(x * this.state.sceneScale);
  };
  translationY = (y: number) => {
    return Math.floor(y * this.state.sceneScale);
  };

  onDropped(objectState: StateObject | void) {
    //If there are not state changes occuring
    if (!objectState) {
      return;
    }

    if (objectState.initialState && objectState.postState)
      if (this.currentState == objectState.initialState)
        this.setItemState(objectState.postState);

    if (objectState.position)
      if (
        objectState.position.x != 0 ||
        objectState.position.y != 0 ||
        objectState.position.z != 0
      )
        this.setItemPosition(objectState.position);
  }

  getButtons = () => {
    return this.props.buttons;
  };

  getLabel = () => {
    return this.props.label;
  };
  render() {
    const pos: React.CSSProperties = {
      //@ts-expect-error: Setting css scene-scale
      "--scene-scale": `${(this.state.sceneScale * 100).toFixed(0)}%`,
      transform:
        `translateX(` +
        calculateXOffset(this.state.position.x) +
        ") translateY(" +
        calculateYOffset(this.state.position.y) +
        `)`,
      position: "absolute",
      zIndex: this.props.position.z,
    };

    // eslint-disable-next-line
    const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
      SimManager.getInstance().handleDragStart(this.props.id);
      this.controls.dragStart(e);
    };

    // eslint-disable-next-line
    const dragging = (e: React.DragEvent<HTMLDivElement>) => {
      this.controls.dragging(e, this);
    };

    // eslint-disable-next-line
    const onDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
      this.controls.dragEnd(e, handleDrop, this.props.id);
    };

    // eslint-disable-next-line
    const handleDrop = (div: HTMLDivElement | null) => {
      //Send message to sim manager to update state of the item that has been
      //dropped on
      if (div) SimManager.getInstance().handleItemDrop(this.props.id, div.id);
    };

    return (
      <div>
        <div
          aria-label={this.state.ariaLabel}
          role="button"
          tabIndex={0}
          id={this.props.id}
          style={pos}
          draggable="true"
          onDragStart={onDragStart}
          {...(isFirefox ? { onDragOver: dragging } : { onDrag: dragging })}
          onDragEnd={onDragEnd}
        >
          {this.props.customComponentID !== "pipette" && (
            <InteractableButtons
              scope={this.props.id}
              buttons={this.getButtons()}
              label={this.getLabel()}
              scale={1}
              visible={true}
              moveMenu={this.props.moveMenu}
              onOpenMoveMenu={this.moveMenuPressed}
              onSetHamburgerButtonRef={this.setHamburgerButtonRef}
            />
          )}
          {this.component}
        </div>
        <MoveMenu
          item={this}
          ref={this.moveMenuRef}
          hamburgerButtonRef={this.hamburgerButtonRef}
        />
      </div>
    );
  }
}

export default CustomObject;
